import { useEffect, useRef, useState, useCallback, type RefObject } from 'react';
import { useViewMode } from 'store/app/appStore';
import { SECTION_NAMES } from '@repo/common';

const useScrollDetails = (isActive: boolean, name: string, gameCount: number, lobbyContainerRef?: RefObject<HTMLDivElement | null>) => {
  const viewMode = useViewMode();
  const isInSingleView = viewMode === 'single';
  const gridContainerRef = useRef<HTMLDivElement>(null);

  const [scrollArrows, setScrollArrows] = useState({
    isHovered: false,
    showLeftArrow: false,
    showRightArrow: false,
  });


  // Throttle helper
  const throttledRef = useRef<NodeJS.Timeout | null>(null);

  // Track programmatic (arrow-triggered) scrolls to prevent arrow flicker during animation
  const isProgrammaticScrollRef = useRef(false);
  const scrollEndTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Use refs to avoid recreating the callback
  const scrollArrowsRef = useRef(scrollArrows);
  scrollArrowsRef.current = scrollArrows;

  const updateArrowVisibility = useCallback(() => {
    if (!gridContainerRef.current || isInSingleView) return;

    const container = gridContainerRef.current;
    const scrollLeft = container.scrollLeft;
    const maxScrollLeft = container.scrollWidth - container.offsetWidth;
    const rightScrollOffset = 10;
    const leftScrollOffset = 1;

    const isAtLeftEdge = scrollLeft <= leftScrollOffset;
    const isAtRightEdge = Math.ceil(scrollLeft) >= maxScrollLeft - rightScrollOffset;

    setScrollArrows(prev => ({
      ...prev,
      showLeftArrow: !isAtLeftEdge,
      showRightArrow: !isAtRightEdge
    }));
  }, [isInSingleView]);

  const handleScroll = useCallback(() => {
    if (!gridContainerRef.current) return;

    if (throttledRef.current) return;

    throttledRef.current = setTimeout(() => {
      throttledRef.current = null;

      if (!gridContainerRef.current || isInSingleView) return;

      // In multi-view: update arrow visibility when hovered, but skip during programmatic
      // scroll to prevent arrow flicker during smooth scroll animation
      if (scrollArrowsRef.current.isHovered && !isProgrammaticScrollRef.current) {
        updateArrowVisibility();
      }
    }, 50);
  }, [isInSingleView, updateArrowVisibility]);

  const handleMouseEnter = useCallback(() => {
    if (!isInSingleView) {
      setScrollArrows(prev => ({ ...prev, isHovered: true }));
      requestAnimationFrame(() => {
        updateArrowVisibility();
      });
    }
  }, [isInSingleView, updateArrowVisibility]);

  const handleMouseLeave = useCallback(() => {
    setScrollArrows(prev => ({
      ...prev,
      isHovered: false,
      showLeftArrow: false,
      showRightArrow: false
    }));
  }, []);

  // Helper function to get container scroll data
  const getScrollData = useCallback(() => {
    if (!gridContainerRef.current) return null;

    const container = gridContainerRef.current;
    const currentScroll = container.scrollLeft;
    const containerItems = Array.from(container.children) as HTMLElement[];
    const maxScroll = container.scrollWidth - container.offsetWidth;

    return { container, currentScroll, containerItems, maxScroll };
  }, []);

  // Calculate how many items are fully visible in the container
  const getVisibleItemCount = useCallback((containerItems: HTMLElement[], currentScroll: number, containerWidth: number) => {
    const visibleCount = containerItems.filter(item =>
      item.offsetLeft >= currentScroll &&
      item.offsetLeft + item.offsetWidth <= currentScroll + containerWidth
    ).length;
    return Math.max(1, visibleCount);
  }, []);

  // Helper function to perform scroll with arrow updates
  const performScroll = useCallback((newScrollLeft: number, container: HTMLElement) => {
    isProgrammaticScrollRef.current = true;

    // Clear any pending scroll-end detection from a previous scroll
    if (scrollEndTimeoutRef.current) {
      clearTimeout(scrollEndTimeoutRef.current);
      scrollEndTimeoutRef.current = null;
    }

    const onScrollEnd = () => {
      isProgrammaticScrollRef.current = false;
      updateArrowVisibility();
    };

    // Use scrollend event to detect when the smooth scroll animation truly finishes
    const handleScrollEnd = () => {
      if (scrollEndTimeoutRef.current) {
        clearTimeout(scrollEndTimeoutRef.current);
        scrollEndTimeoutRef.current = null;
      }
      container.removeEventListener('scrollend', handleScrollEnd);
      onScrollEnd();
    };

    container.addEventListener('scrollend', handleScrollEnd, { once: true });

    // Fallback timeout in case scrollend doesn't fire (e.g. scroll was already at target)
    scrollEndTimeoutRef.current = setTimeout(() => {
      container.removeEventListener('scrollend', handleScrollEnd);
      onScrollEnd();
    }, 800);

    container.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
  }, [updateArrowVisibility]);

  const scrollLeft = useCallback(() => {
    const scrollData = getScrollData();
    if (!scrollData) return;

    const { container, currentScroll, containerItems } = scrollData;

    const scrollStep = getVisibleItemCount(containerItems, currentScroll, container.offsetWidth);

    const indexOfFirstVisible = containerItems.findIndex(item => item.offsetLeft >= currentScroll);
    const targetIndex = Math.max(0, indexOfFirstVisible - scrollStep);
    const targetElement = containerItems[targetIndex];

    // For TOP section, account for the number image that extends to the left of the first card
    const isTopSection = name === SECTION_NAMES.TOP || name === SECTION_NAMES.MOST_PLAYED;
    let newScrollLeft = 0;
    
    if (targetElement) {
      if (targetIndex === 0 && isTopSection) {
        // Scroll to show the number image (negative offset) for the first card
        newScrollLeft = 0;
      } else {
        // Default behavior - scroll to beginning of card
        newScrollLeft = targetElement.offsetLeft;
      }
    }

    newScrollLeft = Math.max(0, newScrollLeft);

    if (newScrollLeft === 0) {
      setScrollArrows(prev => ({ ...prev, showLeftArrow: false, showRightArrow: true }));
    }

    performScroll(newScrollLeft, container);
  }, [getScrollData, performScroll, getVisibleItemCount, name]);

  const scrollRight = useCallback(() => {
    const scrollData = getScrollData();
    if (!scrollData) return;

    const { container, currentScroll, containerItems, maxScroll } = scrollData;

    // Find the last fully visible item
    const lastFullyVisibleIndex = containerItems.findLastIndex(item =>
      item.offsetLeft + item.offsetWidth <= currentScroll + container.offsetWidth
    );

    const scrollStep = getVisibleItemCount(containerItems, currentScroll, container.offsetWidth);

    const targetIndex = Math.min(
      lastFullyVisibleIndex + scrollStep,
      containerItems.length - 1
    );

    const targetElement = containerItems[targetIndex];

    let scrollAmount = 0;
    if (targetElement) {
      // Calculate scroll to make target element fully visible
      const targetRight = targetElement.offsetLeft + targetElement.offsetWidth;
      const viewportRight = currentScroll + container.offsetWidth;

      if (targetRight > viewportRight) {
        scrollAmount = targetRight - viewportRight;
      }
    }

    // Add spacing after scroll: 5px for regular cards, container padding for last card
    if (targetIndex === containerItems.length - 1) {
      const containerStyle = getComputedStyle(container);
      const paddingRight = parseInt(containerStyle.paddingRight) || 0;
      scrollAmount += paddingRight;
    } else {
      scrollAmount += 5;
    }

    // If we're near the end, just scroll to maxScroll to ensure last card is visible
    const newScrollLeft = Math.min(maxScroll, currentScroll + scrollAmount);

    if (container.scrollLeft === 0 && newScrollLeft > 0) {
      setScrollArrows(prev => ({ ...prev, showLeftArrow: true }));
    }

    if (newScrollLeft >= maxScroll - 10) {
      setScrollArrows(prev => ({ ...prev, showRightArrow: false }));
    }

    performScroll(newScrollLeft, container);
  }, [getScrollData, performScroll, getVisibleItemCount]);

  // Attach scroll and wheel listeners
  useEffect(() => {
    const container = gridContainerRef.current;
    if (!container) return;

    // Attach listener for active single-view sections or all multi-view sections
    const shouldListen = (isInSingleView && isActive) || !isInSingleView;
    if (!shouldListen) return;

    container.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll, isInSingleView, isActive]);

  // Entering a section in single view always starts at the top
  useEffect(() => {
    if (isInSingleView && isActive) {
      // Use the ref if available, otherwise fallback to DOM selector
      if (lobbyContainerRef?.current) {
        lobbyContainerRef.current.scrollTop = 0;
      } else {
        const singleSection = document.querySelector('[data-view="single"][data-section-container]');
        if (singleSection) {
          singleSection.scrollTop = 0;
        }
      }
    }
  }, [isInSingleView, isActive, lobbyContainerRef]);

  // Update arrow visibility on mount and when games change
  useEffect(() => {
    if (!isInSingleView) {
      updateArrowVisibility();
    }
  }, [isInSingleView, gameCount, updateArrowVisibility]);

  // Handle window resize to update arrow visibility (debounced)
  useEffect(() => {
    if (isInSingleView) return;

    let resizeTimeout: ReturnType<typeof setTimeout> | null = null;

    const handleResize = () => {
      if (scrollArrows.isHovered) {
        if (resizeTimeout) clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          updateArrowVisibility();
        }, 150);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeout) clearTimeout(resizeTimeout);
    };
  }, [isInSingleView, scrollArrows.isHovered, updateArrowVisibility]);

  return {
    gridContainerRef,
    scrollArrows,
    scrollLeft,
    scrollRight,
    handleMouseEnter,
    handleMouseLeave,
  };
};

export default useScrollDetails;
